<template>
  <UiListItem
    :value="item.id"
    :min-height="48"
    :color="item.color ?? 'text-accent'"
    v-bind="currentLink"
    :disabled="item.disabled"
    :active="activeLinkComparison(item.link)"
    class="ui-list-item"
    active-class="ui-list-item-active"
    @click.capture="item?.callback"
  >
    <template v-if="item.icon" #prepend>
      <UiBadge
        v-if="showBadge && !isSidebarOpen"
        :content="getShortBadge(item.badge)"
        color="sidebar-badge-bg-color"
        text-color="sidebar-badge-text-color"
      >
        <UiIcon :icon="item.icon" />
      </UiBadge>
      <UiIcon v-else :icon="item.icon" />
    </template>
    <UiListItemTitle>
      <AppTranslate :identifier="item.label" />
    </UiListItemTitle>
    <template v-if="showBadge && isSidebarOpen" #append>
      <AppSidebarBadge :count="item.badge" />
    </template>
  </UiListItem>
</template>

<script setup lang="ts">
import { getShortBadge } from '~/utils/badge/index.js';
import { UiBadge, UiIcon, UiListItem, UiListItemTitle } from '~/ui/index.js';
import AppSidebarBadge from '~/app/layouts/sidebar/components/AppSidebarBadge.vue';
import { AppTranslate, useRoute } from '~/infrastructure/index.js';
import { type SidebarItem } from '~/app/layouts/sidebar';
import { computed } from 'vue';
import { isExternalLink } from '~/utils';

export interface Props {
  item: SidebarItem;
  isSidebarOpen: boolean;
}

const props = defineProps<Props>();

const route = useRoute();

const showBadge = computed(
  () => props.item.badge !== undefined && +props.item.badge !== 0,
);

const currentLink = computed(() => {
  const link = props.item?.link;

  if (link !== undefined) {
    return isExternalLink(link)
      ? { href: link, target: '_blank' }
      : { to: link };
  }

  return undefined;
});

const activeLinkComparison = (link: string | undefined): boolean => {
  if (link === undefined) return false;

  if (link.includes('warranty')) {
    return link.includes('warranty') && route.path.includes('warranty');
  }

  if (link.includes('orders')) {
    return route.path.includes(link.split('?')[0]);
  }

  return link === route.path;
};
</script>

<style lang="scss" scoped>
.ui-list-item {
  :deep(.v-icon) {
    opacity: 1;
  }
}
</style>
